import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, Square, Thermometer, Droplets, Wind, Shield, Sparkles, Maximize2, Palette, ArrowRight, Zap } from "lucide-react";
import { ProminentCTA } from "@/components/ui/prominent-cta";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import { SEO } from "@/components/SEO";
import { ProductMetadata } from "@/components/ProductMetadata";
import { getProductByName } from "@/data/productReference";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { InstallationGuidePDF } from "@/components/InstallationGuidePDF";

// K2 Product Images
import k2HeroDesert from "@/assets/k2-hero-desert.jpg";
import k2QuadrateFeatures from "@/assets/k2-quadrate-features.jpg";
import k2KeySellingPoints from "@/assets/k2-key-selling-points.jpg";
import k2LifestyleLuxury from "@/assets/k2-lifestyle-luxury.jpg";
import k2ModernizeSquare from "@/assets/k2-modernize-square.jpg";
import k2SeatRingComfort from "@/assets/k2-seat-ring-comfort.jpg";
import k2ShortenWiden from "@/assets/k2-shorten-widen.jpg";
import k2ColorOptions from "@/assets/k2-color-options.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function K2() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  const productData = getProductByName("K2");

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productImages = [
    { src: k2HeroDesert, alt: "K2 Quadrate smart toilet with revolutionary square design in desert landscape setting", title: "Square Design" },
    { src: k2LifestyleLuxury, alt: "K2 Quadrate smart toilet in elegant luxury bathroom with plants and modern decor", title: "Lifestyle Elegance" },
    { src: k2ModernizeSquare, alt: "K2 Quadrate smart toilet showing futuristic square technology design", title: "Modern Aesthetics" },
    { src: k2ColorOptions, alt: "K2 Quadrate smart toilet available in white and silver grey color options", title: "Color Options" },
  ];

  const premiumFeatures = [
    {
      icon: <Square className="w-8 h-8 text-amber-500" />,
      title: "Revolutionary Square Design",
      description: "Break away from tradition with our innovative quadrate shape - modern aesthetics that complement contemporary bathroom interiors"
    },
    {
      icon: <Thermometer className="w-8 h-8 text-orange-400" />,
      title: "Instant Heating Water",
      description: "Advanced tankless instant water heating system provides unlimited warm water at your preferred temperature"
    },
    {
      icon: <Droplets className="w-8 h-8 text-cyan-400" />,
      title: "Massage Wash System",
      description: "Multi-mode cleansing with massage function, adjustable pressure and oscillating spray patterns"
    },
    {
      icon: <Wind className="w-8 h-8 text-cyan-400" />,
      title: "Warm Air Drying",
      description: "Gentle warm air drying system with adjustable temperature for complete hands-free comfort"
    },
    {
      icon: <Shield className="w-8 h-8 text-green-400" />,
      title: "Square Seat Ring",
      description: "Wider seat ring with uniform support reduces sitting fatigue and provides superior comfort"
    },
    {
      icon: <Sparkles className="w-8 h-8 text-purple-400" />,
      title: "Heated Seat Comfort",
      description: "Premium heated seat with even heat distribution for luxurious comfort in any season"
    }
  ];

  const keyFeatures = [
    "Revolutionary square toilet design",
    "Square water tank with strong flush power",
    "No water pressure limit required",
    "Instant tankless water heating",
    "Massage wash bidet function",
    "Warm air drying system",
    "Heated seat with temperature control",
    "40mm shorter - saves space",
    "Wider square seat ring for comfort",
    "Available in White and Silver Grey",
    "Rectilinear modern aesthetics",
    "Premium ceramic construction"
  ];

  const specifications = [
    { label: "Dimensions", value: "660mm (length) × 400mm × 520mm" },
    { label: "Compared to Standard", value: "40mm shorter front" },
    { label: "Design", value: "Square/Quadrate Shape" },
    { label: "Water Tank", value: "Square design - no pressure limit" },
    { label: "Seat Type", value: "Wide square seat ring" },
    { label: "Color Options", value: "White / Silver Grey" },
    { label: "Heating", value: "Instant tankless system" },
    { label: "Safety", value: "IPX4 Waterproof" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "K2 Quadrate Smart Toilet",
    "description": "Revolutionary square design smart toilet featuring innovative quadrate aesthetics, instant heating, massage wash, and space-saving design. Available in White and Silver Grey.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/k2-hero-desert.jpg"
    ],
    "gtin13": "0795479810018",
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading"
    },
    "sku": "K2-QUADRATE",
    "mpn": "K2",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/k2",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 10,
            "maxValue": 40,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 text-foreground">
      <SEO
        title="K2 Quadrate Smart Toilet | Square Design, CE Certified"
        description="K2 Quadrate smart toilet: square architectural design, instant heating, massage wash and a 40mm shorter footprint. White or Silver Grey. CE certified."
        keywords="K2 smart toilet, square toilet design, quadrate smart toilet, modern toilet design, space saving toilet, instant heating toilet, massage wash bidet, European smart toilet, luxury bathroom 2025"
        url="/products/k2"
        image="https://www.aureum7trading.com/images/products/k2-hero-desert.jpg"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "K2 Quadrate", url: "/products/k2" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section with Desert Image */}
      <section className="relative pt-20 pb-0 overflow-hidden">
        <div className="relative w-full h-[70vh] md:h-[80vh]">
          <img 
            src={k2HeroDesert} 
            alt="K2 Quadrate smart toilet featuring revolutionary square design with modern aesthetics in dramatic desert landscape"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
            <div className="container mx-auto max-w-7xl">
              <Badge variant="outline" className="border-amber-300/50 text-white bg-amber-500/20 backdrop-blur-sm mb-4 font-medium">
                Revolutionary Square Design
              </Badge>
              <h1 className="font-playfair text-5xl md:text-7xl font-light mb-4 text-white tracking-tight">
                K2 Quadrate
                <span className="block text-2xl md:text-3xl text-amber-200 font-medium mt-2">
                  New Aesthetics • New Horizon
                </span>
              </h1>
              <PriceFrom price={600} tone="light" />
              <p className="font-inter text-xl text-white/90 max-w-2xl mb-6">
                Square design meets cutting-edge technology. Experience the future of bathroom aesthetics.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
                  <a href="#features">Explore Features</a>
                </Button>
                <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10" asChild>
                  <LocalizedLink to="/contact">Request Quote</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quadrate Features Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer" id="features">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-4xl md:text-5xl font-light mb-6 text-primary">
              Quadrate<br />New Horizon
            </h2>
            <p className="font-inter text-xl text-muted-foreground mb-8">
              Experience upgrade with our revolutionary square toilet design. The K2 features a completely new shape with square seat ring for strong bearing capacity and comfortable sitting, plus a square water tank for powerful flush with no water pressure limit.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-white rounded-xl shadow-soft">
                <Square className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="font-medium text-sm">Square Seat Ring</p>
                <p className="text-xs text-muted-foreground">Superior comfort</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-soft">
                <Droplets className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
                <p className="font-medium text-sm">Square Water Tank</p>
                <p className="text-xs text-muted-foreground">Powerful flush</p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src={k2QuadrateFeatures} 
              alt="K2 Quadrate features showing new square toilet shape, square seat ring for comfort, and square water tank for powerful flushing"
              className="w-full h-auto rounded-2xl shadow-luxury"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Key Selling Points */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={k2KeySellingPoints} 
                alt="K2 key features including massage wash system, instant heating water, warm air drying, and heated seat technology"
                className="w-full h-auto rounded-2xl shadow-luxury"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-8">Key Selling Points</h2>
              <div className="grid grid-cols-2 gap-6">
                {premiumFeatures.slice(0, 4).map((feature, index) => (
                  <div key={index} className="p-4 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-100">
                    {feature.icon}
                    <h3 className="font-medium mt-3 mb-1 text-sm">{feature.title}</h3>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Square Seat Ring Comfort */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Square Seat Ring<br />Comfortable
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Wider seat ring with uniform support reduces sitting fatigue. The revolutionary square seat design provides even weight distribution for ultimate comfort during use.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <span className="text-muted-foreground">Wider seat ring for uniform support</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <span className="text-muted-foreground">Reduces sitting fatigue</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <span className="text-muted-foreground">Premium ceramic construction</span>
              </li>
            </ul>
          </div>
          <div>
            <img 
              src={k2SeatRingComfort} 
              alt="K2 square seat ring showing wider comfortable design with uniform support for reduced sitting fatigue"
              className="w-full h-auto rounded-2xl shadow-luxury"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Shorten / Widen - Space Saving */}
      <section className="py-16 px-6 lg:px-12 bg-stone-100/50">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={k2ShortenWiden} 
                alt="K2 space-saving design comparison showing 40mm shorter length at 660mm versus standard 700mm toilet"
                className="w-full h-auto rounded-2xl shadow-luxury"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Shorten / Widen
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Shortening saves space without compromising comfort. The K2 is 40mm shorter at the front (660mm vs 700mm standard) while featuring a wider square seat ring for enhanced comfort.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl text-center">
                  <Maximize2 className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="font-bold text-2xl text-primary">660mm</p>
                  <p className="text-sm text-muted-foreground">K2 Length</p>
                </div>
                <div className="bg-white/50 p-4 rounded-xl text-center">
                  <Maximize2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="font-bold text-2xl text-muted-foreground">700mm</p>
                  <p className="text-sm text-muted-foreground">Standard Length</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Color Options */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Fashion Color<br />Free Style
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Rectilinear square design creates stable and balanced visual effects. Choose from two sophisticated color options to match your bathroom aesthetic.
            </p>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-soft">
                <div className="w-6 h-6 bg-white border-2 border-stone-200 rounded-full"></div>
                <span className="font-medium">White</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-soft">
                <div className="w-6 h-6 bg-stone-400 rounded-full"></div>
                <span className="font-medium">Silver Grey</span>
              </div>
            </div>
          </div>
          <div>
            <img 
              src={k2ColorOptions} 
              alt="K2 Quadrate smart toilet available in White and Silver Grey fashion colors with rectilinear square design"
              className="w-full h-auto rounded-2xl shadow-luxury"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Product Reference Information */}
      {productData && (
        <section className="py-8 px-6 lg:px-12 bg-muted/20 container mx-auto max-w-4xl fade-in-observer">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-playfair text-xl font-medium text-primary">Product Information</h2>
            <Badge variant="outline" className="text-xs">Reference System Active</Badge>
          </div>
          <ProductMetadata product={productData} showInternalCodes={true} className="mb-4" />
        </section>
      )}

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Design Gallery</h2>
        <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
      </section>

      {/* Premium Features Grid */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Premium Smart Features</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          Experience advanced technology combined with revolutionary square design aesthetics.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {premiumFeatures.map((feature, index) => (
            <FeatureCardSimple
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </section>

      {/* Complete Feature List */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Complete Features</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="shadow-soft bg-white rounded-lg p-6 text-center">
              <Square className="w-8 h-8 text-amber-500 mx-auto mb-3" />
              <p className="font-inter text-muted-foreground">{feature}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">Technical Specifications</h2>
        <div className="max-w-3xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            {specifications.map((spec, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border-b border-amber-200/50">
                <span className="text-sm text-muted-foreground">{spec.label}</span>
                <p className="font-medium text-primary">{spec.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Guide */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-5xl fade-in-observer" id="installation">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Installation Guide</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          Professional installation recommended. Follow these step-by-step instructions for optimal K2 Quadrate setup.
        </p>
        
        {/* Plumbing Requirements */}
        <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 mb-8 shadow-soft">
          <h3 className="font-playfair text-xl font-medium text-primary mb-6 flex items-center gap-2">
            <Droplets className="w-6 h-6 text-cyan-500" />
            Plumbing Requirements
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold text-amber-700">1</span>
                </div>
                <div>
                  <p className="font-medium text-primary">Water Supply</p>
                  <p className="text-sm text-muted-foreground">Cold water connection: 1/2" (G1/2) standard fitting. Water pressure: 0.05-0.75 MPa (no limit with built-in tank)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold text-amber-700">2</span>
                </div>
                <div>
                  <p className="font-medium text-primary">Drain Connection</p>
                  <p className="text-sm text-muted-foreground">S-trap floor drain: 300-400mm rough-in distance. Drain pipe diameter: 100mm standard</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold text-amber-700">3</span>
                </div>
                <div>
                  <p className="font-medium text-primary">Floor Requirements</p>
                  <p className="text-sm text-muted-foreground">Level, solid floor surface required. Minimum clearance: 660mm length × 400mm width</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Zap className="w-4 h-4 text-cyan-700" />
                </div>
                <div>
                  <p className="font-medium text-primary">Electrical Requirements</p>
                  <p className="text-sm text-muted-foreground">220V/50Hz power supply. Dedicated grounded outlet within 1m of toilet. Circuit breaker: 16A recommended</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Shield className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="font-medium text-primary">Safety Clearance</p>
                  <p className="text-sm text-muted-foreground">Minimum 150mm side clearance. Front clearance: 500mm recommended for comfort</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Installation */}
        <div className="bg-white rounded-2xl p-8 shadow-luxury">
          <h3 className="font-playfair text-xl font-medium text-primary mb-6">Step-by-Step Installation</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="border-l-4 border-amber-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 1: Preparation</p>
                <p className="text-sm text-muted-foreground">Turn off water supply. Remove old toilet. Clean floor surface and inspect drain pipe condition.</p>
              </div>
              <div className="border-l-4 border-amber-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 2: Position Flange</p>
                <p className="text-sm text-muted-foreground">Install new wax ring or gasket on drain flange. Ensure proper alignment with 300-400mm rough-in.</p>
              </div>
              <div className="border-l-4 border-amber-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 3: Set Toilet</p>
                <p className="text-sm text-muted-foreground">Carefully lower K2 onto flange. Press firmly to compress wax ring. Level unit using shims if needed.</p>
              </div>
              <div className="border-l-4 border-amber-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 4: Secure Base</p>
                <p className="text-sm text-muted-foreground">Install floor bolts and tighten evenly. Apply silicone sealant around base edge for watertight seal.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 5: Water Connection</p>
                <p className="text-sm text-muted-foreground">Connect braided supply line to inlet valve. Hand-tighten plus 1/4 turn with wrench. Check for leaks.</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 6: Electrical Setup</p>
                <p className="text-sm text-muted-foreground">Plug power cord into grounded outlet. Ensure GFCI protection is active. Test power indicator.</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 7: System Test</p>
                <p className="text-sm text-muted-foreground">Turn on water supply. Perform multiple test flushes. Check all bidet functions and heated seat operation.</p>
              </div>
              <div className="border-l-4 border-green-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 8: Final Check</p>
                <p className="text-sm text-muted-foreground">Verify no leaks at all connections. Test auto functions. Configure user preferences via control panel.</p>
              </div>
            </div>
          </div>
          
          {/* Important Notes */}
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h4 className="font-medium text-primary mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Important Installation Notes
            </h4>
            <ul className="grid md:grid-cols-2 gap-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                Professional installation strongly recommended
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                Ensure bathroom has adequate ventilation
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                Keep electrical connections away from water
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                Use only manufacturer-approved parts
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                3-year warranty requires certified installation
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                Contact us for professional installer referrals
              </li>
            </ul>
          </div>
          
          {/* PDF Download */}
          <div className="mt-6 flex justify-center">
            <InstallationGuidePDF product="K2" />
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-12 px-6 lg:px-12 container mx-auto max-w-4xl fade-in-observer">
        <ShippingTimeInfo />
      </section>

      {/* Comparison CTA */}
      <section className="py-12 px-6 lg:px-12 bg-gradient-to-r from-amber-50 to-teal-50 fade-in-observer">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-2xl md:text-3xl font-medium text-primary mb-4">
            Can't Decide Between Square and Egg Design?
          </h2>
          <p className="font-inter text-muted-foreground mb-6 max-w-2xl mx-auto">
            Compare the K2 Quadrate's revolutionary square design with the S1 Super Egg's award-winning spherical aesthetics.
          </p>
          <Button size="lg" asChild className="bg-gradient-to-r from-amber-500 to-teal-500 hover:from-amber-600 hover:to-teal-600">
            <LocalizedLink to="/compare/k2-vs-s1">
              Compare K2 vs S1
              <ArrowRight className="w-5 h-5 ml-2" />
            </LocalizedLink>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <ProminentCTA 
        title="Experience the K2 Quadrate"
        description="Ready to transform your bathroom with revolutionary square design? Contact our team for pricing and installation."
        primaryCTA={{ text: "Request Quote", href: "/contact" }}
        secondaryCTA={{ text: "View All Models", href: "/smart-toilets" }}
        showContactButtons={true}
      />

      {/* Related Products */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <RelatedProducts currentProduct="K2" />
      </section>

      {/* Contact Form */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30 fade-in-observer">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Get K2 Quadrate Information</h2>
          <p className="font-inter text-muted-foreground text-center mb-8">
            Request pricing, specifications, or schedule a consultation with our team.
          </p>
          <ContactForm />
        </div>
      </section>

      {/* Social Share */}
      <section className="py-8 px-6 container mx-auto max-w-4xl">
        <SocialShare 
          url="https://www.aureum7trading.com/products/k2" 
          title="K2 Quadrate Smart Toilet - Revolutionary Square Design" 
        />
      </section>

      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-full shadow-lg transition-all duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
